import { useEffect } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import {
  ParticipationStatus,
  StudyType,
  useCancelParticipationByParticipant,
  useParticipation,
} from '@app/api';
import {
  Button,
  NotFound,
  OverlayLoader,
  PublicBackground,
  useToast,
} from '@app/core';
import { TextArea } from '@app/form';
import { Analytics } from '@app/analytics';
import { useLoginParticipantWithModal } from '@app/auth';
import { setLocale } from '@app/i18n';

import { AppointmentInfoLine } from './components/AppointmentInfoLine';

const useStyles = createUseStyles((theme: Theme) => ({
  title: {
    ...theme.fonts.h3,
    marginBottom: theme.marginBase * 4,
  },
  imgContainer: {
    textAlign: 'center',
    marginBottom: theme.marginBase * 7,
  },
  image: {
    margin: 'auto',
    width: '100%',
    maxWidth: theme.marginBase * 20,
  },
  button: {
    textAlign: 'center',
    marginTop: theme.marginBase * 7,
  },
  warning: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
}));

export const ParticipantCancel = () => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const login = useLoginParticipantWithModal();

  const { id } = useParams();
  const { data: participation, isLoading: fetching } = useParticipation(id);
  const locale =
    participation && participation.session && participation.session.locale;

  const { mutateAsync: cancelMutation } = useCancelParticipationByParticipant();

  const { i18n } = useTranslation();

  useEffect(() => {
    setLocale(locale, i18n);
  }, [locale, i18n]);

  if (fetching && !participation) {
    return <OverlayLoader />;
  }

  const isUnModerated =
    participation?.session?.studyType === StudyType.UNMODERATED;

  const validationSchema = Yup.object().shape({
    message: Yup.string().required(),
  });

  const initialValues = {
    message: isUnModerated ? undefined : '',
  };

  const cancel = async ({ message }: typeof initialValues) => {
    try {
      if (!participation?.participantId) {
        Analytics.assertionFailed('No Id when canceling');
        return;
      }
      await login(participation.participantId);
      await cancelMutation({ ...participation, message });
      navigate(`confirm`);
    } catch (e) {
      toast.exception(e);
    }
  };

  if (!participation) {
    return (
      <PublicBackground>
        <NotFound />
      </PublicBackground>
    );
  }

  if (participation.status === ParticipationStatus.CANCELED) {
    return (
      <PublicBackground>
        <NotFound
          title={t('booked.cancelledError.title')}
          text={t('booked.cancelledError.message')}
        />
      </PublicBackground>
    );
  }

  if (
    (!isUnModerated && participation.status !== ParticipationStatus.BOOKED) ||
    (isUnModerated &&
      ![ParticipationStatus.ACCEPTED, ParticipationStatus.IN_PROGRESS].includes(
        participation.status,
      ))
  ) {
    return <Navigate to={`/p/${id}`} />;
  }

  return (
    <PublicBackground>
      <div>
        <h1 className={classes.title}>
          <Trans
            i18nKey={
              isUnModerated ? 'booked.abandon.title' : 'booked.cancel.title'
            }
          />
        </h1>
        <AppointmentInfoLine participation={participation} />

        <Formik
          initialValues={initialValues}
          onSubmit={cancel}
          validationSchema={isUnModerated ? undefined : validationSchema}
        >
          <Form>
            {!isUnModerated && (
              <TextArea
                name="message"
                label={t(
                  isUnModerated
                    ? 'booked.abandon.messageLabel'
                    : 'booked.cancel.messageLabel',
                )}
              />
            )}
            <p className={classes.warning}>
              <Trans i18nKey="booked.cancel.warning" />
            </p>
            <div className={classes.button}>
              <Button color="red" type="submit" key="submit">
                <Trans
                  i18nKey={
                    isUnModerated
                      ? 'booked.abandon.confirm'
                      : 'booked.cancel.confirm'
                  }
                />
              </Button>
            </div>
          </Form>
        </Formik>
      </div>
    </PublicBackground>
  );
};
